import { useEffect, useMemo, useState } from 'react';
import { Info, Loader2, Save, Trash2, X } from 'lucide-react';

import ActionButton from '../../../../../components/Buttons/ActionButton';
import CompactInputField from '../../../../../components/Forms/Input/CompactInputField';
import FormModal from '../../../../../components/Modals/FormModal';
import InputField from '../../../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../../../components/Forms/Input/SimpleSearchSelect';
import TextAreaField from '../../../../../components/Forms/Input/TextAreaField';
import ToggleSwitch from '../../../../../components/Forms/Input/ToggleSwitch';

import useAlerts from '../../../../../hooks/useAlerts';
import useApiErrorHandler from '../../../../../hooks/useApiErrorHandler';
import RestaurantCatalogExtensionService from '../../../../../services/restaurant/RestaurantCatalogExtensionService';
import { RestaurantComboDTO } from '../../../../../types/restaurant/RestaurantCatalogExtensionTypes';
import { RestaurantMenuItemDTO } from '../../../../../types/restaurant/RestaurantMenuTypes';
import { formatMenuConfigurationMoney } from '../menuConfigurationUtils';

interface Props {
  open: boolean;
  branchId: number;
  combo: RestaurantComboDTO | null;
  menuItems: RestaurantMenuItemDTO[];
  menuItemCache: Record<number, RestaurantMenuItemDTO>;
  menuSearch: string;
  onMenuSearchChange: (value: string) => void;
  onClose: () => void;
  onSaved: () => void;
}

interface ComboItemDraft {
  menuItemId: number;
  quantity: number;
}

interface ComboFormState {
  name: string;
  description: string;
  price: string;
  displayOrder: number;
  available: boolean;
  active: boolean;
  items: ComboItemDraft[];
}

const EMPTY_FORM: ComboFormState = {
  name: '',
  description: '',
  price: '',
  displayOrder: 0,
  available: true,
  active: true,
  items: [],
};

const ComboModal = ({
  open,
  branchId,
  combo,
  menuItems,
  menuItemCache,
  menuSearch,
  onMenuSearchChange,
  onClose,
  onSaved,
}: Props) => {
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();

  const [form, setForm] = useState<ComboFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    onMenuSearchChange('');
    if (combo) {
      setForm({
        name: combo.name,
        description: combo.description ?? '',
        price: String(combo.price ?? 0),
        displayOrder: combo.displayOrder ?? 0,
        available: combo.available,
        active: combo.active,
        items: combo.items
          .slice()
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map((item) => ({
            menuItemId: item.menuItemId,
            quantity: Math.max(1, item.quantity),
          })),
      });
    } else {
      setForm(EMPTY_FORM);
    }
    // onMenuSearchChange es estable (setState del hook); evitamos re-ejecutar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, combo]);

  /** Nombre / precio de un ítem: catálogo actual → caché → metadatos del combo. */
  const itemMeta = useMemo(() => {
    const map = new Map<number, { name: string; code: string; price: number }>();
    const put = (id: number, name: string, code: string, price: number) => {
      map.set(id, { name, code, price });
    };
    Object.values(menuItemCache).forEach((i) =>
      put(i.id, i.displayName || i.productName, i.code, i.productPrice)
    );
    menuItems.forEach((i) =>
      put(i.id, i.displayName || i.productName, i.code, i.productPrice)
    );
    combo?.items.forEach((i) => {
      if (!map.has(i.menuItemId)) {
        put(i.menuItemId, i.menuItemName, i.menuItemCode, i.menuItemPrice);
      }
    });
    return map;
  }, [menuItemCache, menuItems, combo]);

  const addOptions = useMemo(
    () =>
      menuItems
        .filter((i) => !form.items.some((it) => it.menuItemId === i.id))
        .map((i) => ({
          id: i.id,
          name: i.displayName || i.productName,
          code: i.code,
        })),
    [menuItems, form.items]
  );

  const addItem = (menuItemId: number) => {
    setForm((c) =>
      c.items.some((i) => i.menuItemId === menuItemId)
        ? c
        : { ...c, items: [...c.items, { menuItemId, quantity: 1 }] }
    );
    onMenuSearchChange('');
  };

  const removeItem = (menuItemId: number) => {
    setForm((c) => ({
      ...c,
      items: c.items.filter((i) => i.menuItemId !== menuItemId),
    }));
  };

  const setQuantity = (menuItemId: number, value: number) => {
    const quantity = Math.max(1, Math.trunc(value || 1));
    setForm((c) => ({
      ...c,
      items: c.items.map((i) =>
        i.menuItemId === menuItemId ? { ...i, quantity } : i
      ),
    }));
  };

  const separatePrice = useMemo(
    () =>
      form.items.reduce((total, item) => {
        const meta = itemMeta.get(item.menuItemId);
        return total + (meta ? meta.price * item.quantity : 0);
      }, 0),
    [form.items, itemMeta]
  );
  const savings = separatePrice - Number(form.price || 0);

  const save = async () => {
    const name = form.name.trim();
    if (!name) {
      show('error', 'Falta el nombre', 'Escribe un nombre para el combo.');
      return;
    }
    if (!form.items.length) {
      show(
        'error',
        'Sin productos',
        'Agrega al menos un plato al combo.'
      );
      return;
    }
    const price = Number(form.price);
    if (!Number.isFinite(price) || price < 0) {
      show('error', 'Precio inválido', 'Ingresa un precio mayor o igual a cero.');
      return;
    }

    setSaving(true);
    try {
      const body = {
        code: combo?.code ?? '',
        name,
        description: form.description.trim() || null,
        price,
        available: form.available,
        displayOrder: Math.max(0, Number(form.displayOrder) || 0),
        active: form.active,
        items: form.items.map((item, index) => ({
          menuItemId: item.menuItemId,
          quantity: item.quantity,
          displayOrder: index,
        })),
      };
      const response = combo
        ? await RestaurantCatalogExtensionService.updateCombo(
            branchId,
            combo.id,
            body
          )
        : await RestaurantCatalogExtensionService.createCombo(branchId, body);
      onSaved();
      show(
        'success',
        'Combo guardado',
        response.message ||
          (combo ? 'El combo se actualizó.' : 'El combo se creó correctamente.')
      );
      onClose();
    } catch (error) {
      handle(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      isOpen={open}
      onClose={saving ? undefined : onClose}
      title={combo ? `Editar combo · ${combo.name}` : 'Nuevo combo'}
      maxWidth="max-w-3xl"
      zIndex="top"
    >
      <div className="space-y-6">
        {/* Datos generales */}
        <div className="grid gap-x-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <InputField
              label="Nombre del combo"
              name="comboName"
              type="text"
              iconKey="product"
              value={form.name}
              disabled={saving}
              placeholder="Ej. Combo familiar"
              onChange={(e) =>
                setForm((c) => ({ ...c, name: e.target.value.slice(0, 150) }))
              }
            />
          </div>
          <InputField
            label="Precio del combo"
            name="comboPrice"
            type="number"
            iconKey="dollar"
            min={0}
            step="0.01"
            value={form.price}
            disabled={saving}
            placeholder="0.00"
            onChange={(e) => setForm((c) => ({ ...c, price: e.target.value }))}
          />
          <InputField
            label="Orden de visualización"
            name="comboOrder"
            type="number"
            iconKey="stock"
            min={0}
            step="1"
            value={form.displayOrder}
            disabled={saving}
            onChange={(e) =>
              setForm((c) => ({
                ...c,
                displayOrder: Math.max(0, Number(e.target.value) || 0),
              }))
            }
          />
          <div className="sm:col-span-2">
            <TextAreaField
              label="Descripción"
              name="comboDescription"
              rows={2}
              value={form.description}
              disabled={saving}
              placeholder="Opcional"
              onChange={(e) =>
                setForm((c) => ({
                  ...c,
                  description: e.target.value.slice(0, 500),
                }))
              }
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6 rounded-xl border border-stroke bg-gray-2/50 px-4 py-3 dark:border-strokedark dark:bg-meta-4/40">
          <ToggleSwitch
            label="Disponible"
            name="comboAvailable"
            checked={form.available}
            disabled={saving}
            onChange={() => setForm((c) => ({ ...c, available: !c.available }))}
          />
          <ToggleSwitch
            label="Activo"
            name="comboActive"
            checked={form.active}
            disabled={saving}
            onChange={() => setForm((c) => ({ ...c, active: !c.active }))}
          />
          <p className="flex items-center gap-1.5 text-xs text-bodydark2">
            <Info className="h-3.5 w-3.5" />
            {combo
              ? `Código interno: ${combo.code}`
              : 'El código se genera automáticamente.'}
          </p>
        </div>

        {/* Platos del combo */}
        <div className="border-t border-stroke pt-5 dark:border-strokedark">
          <p className="mb-3 text-sm font-semibold text-black dark:text-white">
            Platos del combo ({form.items.length})
          </p>

          <SimpleSearchSelect
            label="Agregar plato"
            name="comboAddItem"
            placeholder="Busca un plato por nombre o código…"
            searchPlaceholder="Nombre o código…"
            iconKey="search"
            options={addOptions}
            selectedValue={null}
            disabled={saving}
            onSearch={onMenuSearchChange}
            onSelect={(option) => addItem(option.id)}
          />
          {menuSearch.trim() !== '' && addOptions.length === 0 && (
            <p className="-mt-3 mb-3 text-xs text-bodydark2">
              Ningún plato coincide con la búsqueda.
            </p>
          )}

          {form.items.length === 0 ? (
            <p className="rounded-xl border border-dashed border-stroke px-4 py-6 text-center text-xs text-bodydark2 dark:border-strokedark">
              Aún no hay platos. Búscalos arriba y agrégalos al combo.
            </p>
          ) : (
            <ul className="space-y-2">
              {form.items.map((item) => {
                const meta = itemMeta.get(item.menuItemId);
                const unit = meta?.price ?? 0;
                const lineTotal = unit * item.quantity;
                return (
                  <li
                    key={item.menuItemId}
                    className="flex flex-wrap items-center gap-3 rounded-xl border border-stroke p-3 dark:border-strokedark"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-black dark:text-white">
                        {meta?.name ?? `Plato #${item.menuItemId}`}
                      </p>
                      <p className="mt-0.5 text-xs text-bodydark2">
                        {meta?.code ? `${meta.code} · ` : ''}
                        {formatMenuConfigurationMoney(unit)} c/u
                      </p>
                    </div>

                    <div className="w-24 shrink-0">
                      <CompactInputField
                        label="Cantidad"
                        name={`comboQty-${item.menuItemId}`}
                        type="number"
                        min={1}
                        step="1"
                        value={item.quantity}
                        disabled={saving}
                        onChange={(e) =>
                          setQuantity(item.menuItemId, Number(e.target.value))
                        }
                      />
                    </div>

                    <div className="w-28 shrink-0 text-right">
                      <p className="text-[11px] text-bodydark2">
                        {item.quantity} × {formatMenuConfigurationMoney(unit)}
                      </p>
                      <p className="text-sm font-semibold text-black dark:text-white">
                        {formatMenuConfigurationMoney(lineTotal)}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeItem(item.menuItemId)}
                      disabled={saving}
                      aria-label={`Quitar ${meta?.name ?? 'plato'}`}
                      className="shrink-0 rounded-lg p-2 text-bodydark2 transition hover:bg-danger/10 hover:text-danger"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Resumen económico */}
        {form.items.length > 0 && (
          <dl className="space-y-2 rounded-xl border border-stroke bg-gray-2/50 p-4 text-sm dark:border-strokedark dark:bg-meta-4/40">
            <div className="flex items-center justify-between gap-4">
              <dt className="text-bodydark2">
                Comprando cada plato por separado
              </dt>
              <dd className="font-medium text-black dark:text-white">
                {formatMenuConfigurationMoney(separatePrice)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-4">
              <dt className="text-bodydark2">Precio del combo</dt>
              <dd className="font-semibold text-primary">
                {formatMenuConfigurationMoney(Number(form.price || 0))}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-stroke pt-2 dark:border-strokedark">
              <dt
                className={`font-medium ${
                  savings > 0
                    ? 'text-success'
                    : savings < 0
                    ? 'text-danger'
                    : 'text-bodydark2'
                }`}
              >
                {savings >= 0 ? 'Ahorro para el cliente' : 'Sobreprecio'}
              </dt>
              <dd
                className={`font-semibold ${
                  savings > 0
                    ? 'text-success'
                    : savings < 0
                    ? 'text-danger'
                    : 'text-bodydark2'
                }`}
              >
                {formatMenuConfigurationMoney(Math.abs(savings))}
              </dd>
            </div>
          </dl>
        )}

        <div className="flex justify-end gap-3 border-t border-stroke pt-4 dark:border-strokedark">
          <ActionButton
            label="Cerrar"
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled={saving}
            icon={<X className="h-4 w-4" />}
            onClick={onClose}
          />
          <ActionButton
            label={
              saving ? 'Guardando…' : combo ? 'Guardar cambios' : 'Crear combo'
            }
            variant="primary"
            size="md"
            fullWidth={false}
            disabled={saving}
            icon={
              saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )
            }
            onClick={save}
          />
        </div>
      </div>
    </FormModal>
  );
};

export default ComboModal;
